import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Skeleton } from 'src/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { ConversionsByBeaconRow } from 'src/redux/admin/reporting/reporting.types';
import { formatTokenizedLabel } from 'src/pages/reports/utils/format-reporting-labels';

export interface ConversionsByBeaconTableProps {
  data: ConversionsByBeaconRow[];
  isLoading?: boolean;
}

type SortKey =
  | 'beacon'
  | 'conversion_id'
  | 'conversion_type'
  | 'conversions';
type SortOrder = 'asc' | 'desc';

function formatBeaconLabel(row: ConversionsByBeaconRow): string {
  const name = row.conversion_name?.trim();
  if (name) {
    return formatTokenizedLabel(name);
  }
  return row.conversion_id;
}

function formatTypeLabel(row: ConversionsByBeaconRow): string {
  const type = row.conversion_type?.trim();
  return type ? formatTokenizedLabel(type) : '—';
}

const formatNumber = (num: number): string => {
  if (!isFinite(num) || isNaN(num)) return 'N/A';
  return num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const SortIcon: React.FC<{ active: boolean; order: SortOrder }> = ({ active, order }) => {
  if (!active) return <ArrowUpDown className="ml-1 inline size-3 opacity-50" />;
  return order === 'asc'
    ? <ArrowUp className="ml-1 inline size-3" />
    : <ArrowDown className="ml-1 inline size-3" />;
};

const COLUMNS: Array<{
  key: SortKey;
  label: string;
  align: 'left' | 'right';
}> = [
  { key: 'beacon', label: 'Beacon', align: 'left' },
  { key: 'conversion_id', label: 'Conversion ID', align: 'left' },
  { key: 'conversion_type', label: 'Type', align: 'left' },
  { key: 'conversions', label: 'Conversions', align: 'right' },
];

const TEXT_SORT_KEYS: SortKey[] = ['beacon', 'conversion_id', 'conversion_type'];

function getSortValue(row: ConversionsByBeaconRow, sortBy: SortKey): string | number {
  switch (sortBy) {
    case 'beacon':
      return formatBeaconLabel(row);
    case 'conversion_id':
      return row.conversion_id ?? '';
    case 'conversion_type':
      return formatTypeLabel(row);
    case 'conversions':
      return Number(row.conversions ?? 0);
  }
}

function renderCell(row: ConversionsByBeaconRow, key: SortKey): string {
  switch (key) {
    case 'beacon':
      return formatBeaconLabel(row);
    case 'conversion_id':
      return row.conversion_id || '—';
    case 'conversion_type':
      return formatTypeLabel(row);
    case 'conversions':
      return formatNumber(Number(row.conversions ?? 0));
  }
}

const ConversionsByBeaconTable: React.FC<ConversionsByBeaconTableProps> = ({
  data,
  isLoading = false,
}) => {
  const [sortBy, setSortBy] = useState<SortKey>('conversions');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const sortedRows = useMemo(() => {
    return [...data].sort((a, b) => {
      const aValue = getSortValue(a, sortBy);
      const bValue = getSortValue(b, sortBy);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
      }

      const aStr = String(aValue);
      const bStr = String(bValue);
      return sortOrder === 'desc' ? bStr.localeCompare(aStr) : aStr.localeCompare(bStr);
    });
  }, [data, sortBy, sortOrder]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(key);
    setSortOrder(TEXT_SORT_KEYS.includes(key) ? 'asc' : 'desc');
  };

  return (
    <div className="rounded-2xl bg-white border border-gray-400 p-8">
      <div className="text-2xl font-light mb-4">
        Conversions by Beacon
      </div>
      <div className="overflow-x-auto">
        {isLoading ? (
          <Skeleton className="w-full h-[200px]" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-b border-gray-400 hover:bg-transparent">
                {COLUMNS.map((column) => (
                  <TableHead
                    key={column.key}
                    className={`h-auto py-2 px-4 text-sm font-medium text-gray-600 ${
                      column.align === 'right' ? 'text-right' : 'text-left'
                    } cursor-pointer select-none hover:text-gray-900`}
                    onClick={() => handleSort(column.key)}
                    aria-sort={
                      sortBy === column.key
                        ? sortOrder === 'asc' ? 'ascending' : 'descending'
                        : 'none'
                    }
                  >
                    {column.label}
                    <SortIcon
                      active={sortBy === column.key}
                      order={sortOrder}
                    />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={COLUMNS.length}
                    className="py-8 px-4 text-center text-sm text-gray-500"
                  >
                    No conversion beacon data for this selection
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((row, index) => (
                  <TableRow
                    key={`${row.conversion_id}-${row.conversion_type ?? ''}-${index}`}
                    className="border-b border-gray-200 hover:bg-gray-50"
                  >
                    {COLUMNS.map((column) => (
                      <TableCell
                        key={column.key}
                        className={`py-2 px-4 ${
                          column.align === 'right' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {renderCell(row, column.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default ConversionsByBeaconTable;
